/**
 * Pure variant resolution for the Settings page.
 *
 * Lives in its own React-free module so unit tests can import it
 * without pulling the page's component graph — same split as
 * `components/branching-switcher-label.ts`.
 */

import type { SwitcherSurface } from '@/components/branching-switcher-label'

export type SettingsVariant = 'my-school' | 'billing' | 'my-kitchen' | 'account'

/**
 * Which Settings tabs a persona sees, in display order.
 *
 * `billing` is the School AR panel that also renders at
 * /billing/school. The sidebar's Billing item is gated on
 * `invoice.read` / `payout.read` (see `packages/access/src/nav.ts`),
 * which school admins do not carry — so the route existed with no way
 * in. Mounting it as a Settings tab exposes it without widening the
 * permission model; the endpoint behind it is already scoped to the
 * actor's SCHOOL_ADMIN memberships.
 */
export function resolveSettings(profileCodes: string[], isOrdoStaff = false): SettingsVariant[] {
  const out: SettingsVariant[] = []
  const isOrdoAdmin = isOrdoStaff
  if (isOrdoAdmin || profileCodes.includes('SCHOOL_ADMIN')) {
    out.push('my-school')
    out.push('billing')
  }
  if (isOrdoAdmin || profileCodes.includes('CHEF_ADMIN') || profileCodes.includes('SUPPLIER_ADMIN')) {
    out.push('my-kitchen')
  }
  // Everyone gets the personal Account panel (PROD-27). Entity panels
  // come first so admins keep landing on the panel they manage.
  out.push('account')
  return out
}

/** The variant a Settings URL pins, or null for the bare /settings hub. */
export function settingsVariantForPath(pathname: string): SettingsVariant | null {
  if (pathname.endsWith('/settings/my-school')) {
    return 'my-school'
  }
  if (pathname.endsWith('/settings/billing')) {
    return 'billing'
  }
  if (pathname.endsWith('/settings/my-kitchen')) {
    return 'my-kitchen'
  }
  if (pathname.endsWith('/settings/account')) {
    return 'account'
  }
  return null
}

/** Label source for the BranchingSwitcher button of each variant. */
export function settingsSwitcherSurface(variant: SettingsVariant): SwitcherSurface {
  switch (variant) {
    case 'my-school':
      return 'school'
    case 'billing':
      return 'billing'
    case 'my-kitchen':
      return 'kitchen'
    case 'account':
      return 'account'
  }
}
